import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { organizerAuthApi } from "../lib/api";

interface OrganizerUser {
  id: string;
  email: string;
  displayName: string | null;
  plan: string;
}

interface OrganizerAuthContextValue {
  organizer: OrganizerUser | null;
  isLoading: boolean;
  requestCode: (email: string, turnstileToken: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const OrganizerAuthContext = createContext<OrganizerAuthContextValue | null>(null);

export function OrganizerAuthProvider({ children }: { children: ReactNode }) {
  const [organizer, setOrganizer] = useState<OrganizerUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    organizerAuthApi
      .me()
      .then((res) => {
        if (res.ok && res.data) {
          setOrganizer(res.data.organizer);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const handler = () => setOrganizer(null);
    window.addEventListener("organizer:session-expired", handler);
    return () => window.removeEventListener("organizer:session-expired", handler);
  }, []);

  const requestCode = useCallback(async (email: string, turnstileToken: string) => {
    await organizerAuthApi.requestCode(email, turnstileToken);
  }, []);

  const verifyCode = useCallback(async (email: string, code: string) => {
    const res = await organizerAuthApi.verifyCode(email, code);
    if (res.ok && res.data) {
      setOrganizer(res.data.organizer);
    }
  }, []);

  const logout = useCallback(async () => {
    await organizerAuthApi.logout();
    setOrganizer(null);
  }, []);

  return (
    <OrganizerAuthContext.Provider
      value={{ organizer, isLoading, requestCode, verifyCode, logout }}
    >
      {children}
    </OrganizerAuthContext.Provider>
  );
}

export function useOrganizerAuth() {
  const ctx = useContext(OrganizerAuthContext);
  if (!ctx) {
    throw new Error("useOrganizerAuth must be used within OrganizerAuthProvider");
  }
  return ctx;
}
